import * as CL from '@design-system/component-library';
import { generatePath, useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import { devicesMsg, lostDeviceMsg, omaElisaForCompaniesMsg, t } from '../../common/i18n/index.js';
import { BreadCrumbsWithTitle } from '../BreadCrumbsWithTitle/BreadCrumbsWithTitle.js';
import { DetailsWrapper } from '../DetailsWrapper/index.js';
import { SelectGroup } from '../SelectGroup/SelectGroup.js';
import { SelectGroupItem } from '../SelectGroup/SelectGroupItem.js';
import { EppDestroyedOrMissingDevice } from './EppDestroyedOrMissingDevice.js';
import { EppSupportOrServiceRequest } from './EppSupportOrServiceRequest.js';
import type { CompanyInfoResponse, Subscription } from '../../generated/api/models.js';

import './EppMaintenance.scss';

export interface EppMaintenanceAdminProps {
  subscription: Subscription;
  companyInfo?: CompanyInfoResponse;
  hasPendingActions: boolean;
}

export const EppMaintenanceAdmin = ({ subscription, companyInfo, hasPendingActions }: EppMaintenanceAdminProps) => {
  const navigate = useNavigate();
  const subscriptionId = subscription.subscriptionDisplayId;
  const { search } = useLocation();
  const onCancel = () => navigate(generatePath(paths.PS_DEVICE, { subscriptionId }) + search);
  const breadCrumbs = (
    <BreadCrumbsWithTitle
      breadCrumbPaths={[
        { name: t.VCUZ(omaElisaForCompaniesMsg), path: paths.SELF_SERVICE_HOME },
        { name: t.JJTM(devicesMsg), path: paths.PS_DEVICES },
        {
          name: subscriptionId,
          path: generatePath(paths.PS_DEVICE, { subscriptionId }) + search,
        },
        { name: t.C7PN('Support request') },
      ]}
    />
  );

  return (
    <DetailsWrapper
      classes={['of-epp-maintenance']}
      detailsTop={breadCrumbs}
      heading={subscription.subscriptionName}
      headingTop={t.N2CC('Device maintenance request')}
      heroPicto="generic-service-filled"
      id="subscription-details"
    >
      <h3>{t.SQYY('Subject of the report')}</h3>
      <SelectGroup className={dsClass.FONT_WEIGHT_600}>
        <SelectGroupItem
          icon={<CL.Icon icon="maintenance" size="m" type="regular" />}
          title={t.E7S2('Damage or maintenance report')}
        >
          <EppSupportOrServiceRequest
            isEmployee={false}
            onCancel={onCancel}
            subscription={subscription}
            companyInfo={companyInfo}
          />
        </SelectGroupItem>
        <SelectGroupItem
          icon={<CL.Icon icon="location-unknown" size="m" type="regular" />}
          title={t.Z7IG(lostDeviceMsg)}
        >
          <EppDestroyedOrMissingDevice
            subscription={subscription}
            onCancel={onCancel}
            companyInfo={companyInfo}
            hasPendingActions={hasPendingActions}
          />
        </SelectGroupItem>
      </SelectGroup>
    </DetailsWrapper>
  );
};
